import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { createBillingCheckoutSession, fetchPublicSiteContent } from '../supabaseClient';
import './SerVSoloLandingPage.css';

const DEFAULT_CONTENT = {
  hero: {
    badge: 'All-in-one venue management',
    title: 'Run your whole venue from one place',
    subtitle: 'EFTPOS payments, online ordering, and a no-code website — all in one dashboard. No technical skills needed.',
    primaryBtn: 'Get Started Free',
    secondaryBtn: 'See Pricing',
    stats: [
      { value: '1%', label: 'EFTPOS commission' },
      { value: '$0', label: 'Setup fee' },
      { value: 'Daily', label: 'Settlement' },
    ],
  },
  featuresSection: {
    eyebrow: 'Features',
    title: "Everything you need, nothing you don't",
  },
  features: [
    { title: 'EFTPOS & Card Payments', desc: 'Accept tap, swipe, and chip payments in-store. Daily settlement straight to your account. Simple 1% commission — no terminal hire fees.' },
    { title: 'Online Ordering & Kiosk', desc: 'Customers order from their phone or a tablet kiosk. Orders go straight to your kitchen — no third-party fees, no commission per order.' },
    { title: 'No-Code Website Builder', desc: 'Build a branded storefront with drag-and-drop blocks. Publish changes live in seconds — no developer or hosting costs required.' },
    { title: 'Products, Modifiers & Staff', desc: 'Full menu management with modifier groups. Set opening hours and toggle kiosk or online ordering on or off at any time.' },
  ],
  pricing: {
    eyebrow: 'Pricing',
    title: 'Simple, transparent pricing',
    subtitle: 'No setup fees. No lock-in contracts. Cancel any time.',
    monthly: { price: '59', billing: 'Billed monthly — cancel any time' },
    annual: { price: '49', billing: 'Billed $588/year' },
  },
  contact: {
    eyebrow: 'Contact',
    title: 'Get in touch',
    subtitle: "Have a question? We'd love to hear from you.",
    email: 'hello@servsolo.com',
    note: 'We typically respond within one business day.',
  },
  stripe: { monthlyPriceId: '', annualPriceId: '' },
  planFeatures: [
    'EFTPOS & card payments',
    'Online ordering & kiosk',
    'No-code website builder',
    'Products & modifiers',
    'Staff & hours controls',
    'Daily EFTPOS settlement',
    'Unlimited orders',
  ],
  planNote: '+ 1% on in-store EFTPOS transactions',
};

const deepMerge = (base, override) => {
  if (!override) return base;
  const result = { ...base };
  Object.keys(override).forEach((k) => {
    if (override[k] && typeof override[k] === 'object' && !Array.isArray(override[k])) {
      result[k] = deepMerge(base[k] || {}, override[k]);
    } else if (override[k] !== undefined) {
      result[k] = override[k];
    }
  });
  return result;
};

const FEATURE_ICONS = [
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>,
];


const SerVSoloLandingPage = ({ isSignedIn = false, scrollToSection = null }) => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [logoUrl, setLogoUrl] = useState('');
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    let active = true;
    fetchPublicSiteContent().then(rows => {
      if (!active) return;
      const brandingRow = rows.find(r => r.page_slug === 'branding');
      const landingRow = rows.find(r => r.page_slug === 'landing');
      if (brandingRow?.content?.logoImageUrl) setLogoUrl(brandingRow.content.logoImageUrl);
      if (landingRow?.content) setContent(deepMerge(DEFAULT_CONTENT, landingRow.content));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!scrollToSection) return;
    const el = document.getElementById(scrollToSection);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      const t = setTimeout(() => document.getElementById(scrollToSection)?.scrollIntoView({ behavior: 'smooth' }), 200);
      return () => clearTimeout(t);
    }
  }, [scrollToSection]);

  const scrollTo = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubscribe = async (interval) => {
    if (!isSignedIn) {
      window.location.href = '/login';
      return;
    }
    setLoadingPlan(interval);
    setCheckoutError('');
    try {
      const priceId = interval === 'monthly' ? content.stripe?.monthlyPriceId : content.stripe?.annualPriceId;
      const result = await createBillingCheckoutSession({
        interval,
        priceId: priceId || undefined,
        successUrl: `${window.location.origin}/dashboard?billing=success`,
        cancelUrl: `${window.location.origin}/#pricing`,
      });
      if (result?.url) {
        window.location.href = result.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch {
      setCheckoutError('Could not start checkout. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="sl-wrap">
      {/* NAV */}
      <nav className="sl-nav">
        <div className="sl-nav-inner">
          <a
            href="/"
            className="sl-logo"
            aria-label="SerVSolo home"
            onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); setMobileMenuOpen(false); }}
          >
            {logoUrl
              ? <img src={logoUrl} alt="SerVSolo" className="sl-logo-img" />
              : <><span className="sl-logo-serv">Serv</span><span className="sl-logo-solo">Solo</span></>
            }
          </a>
          <div className={`sl-nav-links${mobileMenuOpen ? ' sl-nav-open' : ''}`}>
            <button className="sl-nav-link" onClick={() => scrollTo('features')}>Features</button>
            <button className="sl-nav-link" onClick={() => scrollTo('pricing')}>Pricing</button>
            <button className="sl-nav-link" onClick={() => scrollTo('contact')}>Contact</button>
          </div>
          <div className="sl-nav-right">
            <Link to={isSignedIn ? '/dashboard' : '/login'} className="sl-nav-cta">
              {isSignedIn ? 'Dashboard' : 'Get Started'}
            </Link>
            <button className="sl-hamburger" aria-label="Toggle menu" onClick={() => setMobileMenuOpen(o => !o)}>
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="sl-hero" id="hero">
        <div className="sl-container">
          <div className="sl-hero-badge">{content.hero.badge}</div>
          <h1 className="sl-hero-title">{content.hero.title}</h1>
          <p className="sl-hero-sub">{content.hero.subtitle}</p>
          <div className="sl-hero-actions">
            <Link to="/login" className="sl-btn-primary">{content.hero.primaryBtn || 'Get Started Free'}</Link>
            <button className="sl-btn-ghost" onClick={() => scrollTo('pricing')}>{content.hero.secondaryBtn || 'See Pricing'}</button>
          </div>
          <div className="sl-hero-stats">
            {(content.hero.stats || []).map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="sl-stat-divider" />}
                <div className="sl-stat"><strong>{s.value}</strong><span>{s.label}</span></div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="sl-features" id="features">
        <div className="sl-container">
          <p className="sl-section-eyebrow">{content.featuresSection?.eyebrow || 'Features'}</p>
          <h2 className="sl-section-title">{content.featuresSection?.title || "Everything you need, nothing you don't"}</h2>
          <div className="sl-features-grid">
            {content.features.map((f, i) => (
              <div key={i} className="sl-feature-card">
                <div className="sl-feature-icon">{FEATURE_ICONS[i]}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="sl-pricing" id="pricing">
        <div className="sl-container">
          <p className="sl-section-eyebrow">{content.pricing.eyebrow || 'Pricing'}</p>
          <h2 className="sl-section-title">{content.pricing.title || 'Simple, transparent pricing'}</h2>
          <p className="sl-section-sub">{content.pricing.subtitle || 'No setup fees. No lock-in contracts. Cancel any time.'}</p>
          {checkoutError && <p className="sl-checkout-error">{checkoutError}</p>}
          {(() => {
              const monthlyPrice = parseFloat(content.pricing.monthly.price) || 0;
              const annualPrice = parseFloat(content.pricing.annual.price) || 0;
              const savePct = monthlyPrice > 0 && annualPrice < monthlyPrice
                ? Math.round((monthlyPrice - annualPrice) / monthlyPrice * 100)
                : 0;
              const annualTotal = annualPrice > 0 ? Math.round(annualPrice * 12) : 0;
              return (
          <div className="sl-plans">
            {/* MONTHLY */}
            <div className="sl-plan">
              <div className="sl-plan-name">{content.pricing.monthly.label || 'Monthly'}</div>
              <div className="sl-plan-price-row">
                <span className="sl-plan-price">${content.pricing.monthly.price}</span>
                <span className="sl-plan-per">/mo</span>
              </div>
              <div className="sl-plan-billing">{content.pricing.monthly.billing}</div>
              <ul className="sl-plan-features">
                {(content.planFeatures || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
              {content.planNote && <p className="sl-plan-note">{content.planNote}</p>}
              <button
                className="sl-btn-plan"
                onClick={() => handleSubscribe('monthly')}
                disabled={loadingPlan !== null}
              >
                {loadingPlan === 'monthly' ? 'Redirecting...' : 'Get Started'}
              </button>
            </div>

            {/* ANNUAL */}
            <div className="sl-plan sl-plan-featured">
              {savePct > 0 && <div className="sl-plan-badge">Most popular — save {savePct}%</div>}
              <div className="sl-plan-name">{content.pricing.annual.label || 'Annual'}</div>
              <div className="sl-plan-price-row">
                <span className="sl-plan-price">${content.pricing.annual.price}</span>
                <span className="sl-plan-per">/mo</span>
              </div>
              <div className="sl-plan-billing">{annualTotal > 0 ? `Billed $${annualTotal}/year` : content.pricing.annual.billing}</div>
              <ul className="sl-plan-features">
                {(content.planFeatures || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
              {content.planNote && <p className="sl-plan-note">{content.planNote}</p>}
              <button
                className="sl-btn-plan sl-btn-plan-featured"
                onClick={() => handleSubscribe('annual')}
                disabled={loadingPlan !== null}
              >
                {loadingPlan === 'annual' ? 'Redirecting...' : 'Get Started'}
              </button>
            </div>
          </div>
              );
            })()}
        </div>
      </section>

      {/* CONTACT */}
      <section className="sl-contact" id="contact">
        <div className="sl-container">
          <p className="sl-section-eyebrow">{content.contact.eyebrow || 'Contact'}</p>
          <h2 className="sl-section-title">{content.contact.title || 'Get in touch'}</h2>
          <p className="sl-section-sub">{content.contact.subtitle || "Have a question? We'd love to hear from you."}</p>
          <a href={`mailto:${content.contact.email}`} className="sl-contact-email">{content.contact.email}</a>
          <p className="sl-contact-note">{content.contact.note}</p>
          <Link to="/login" className="sl-btn-primary sl-contact-cta">{content.hero.primaryBtn || 'Get Started Free'}</Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="sl-footer">
        <div className="sl-container sl-footer-inner">
          <span className="sl-footer-logo">
            {logoUrl
              ? <img src={logoUrl} alt="SerVSolo" className="sl-footer-logo-img" />
              : <><span className="sl-logo-serv">Serv</span><span className="sl-logo-solo">Solo</span></>
            }
          </span>
          <nav className="sl-footer-links">
            <button className="sl-footer-link" onClick={() => scrollTo('features')}>Features</button>
            <button className="sl-footer-link" onClick={() => scrollTo('pricing')}>Pricing</button>
            <button className="sl-footer-link" onClick={() => scrollTo('contact')}>Contact</button>
            <Link className="sl-footer-link" to="/login">Sign In</Link>
          </nav>
          <span className="sl-footer-copy">© {new Date().getFullYear()} SerVSolo. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
};

export default SerVSoloLandingPage;
